import {
    FilterMenu,
    FilterMenuApply,
    FilterMenuClear,
    FilterMenuContent,
    FilterMenuFooter,
    FilterMenuTrigger,
    type FilterMenuSelection,
} from '@plato/ui/components/filter-menu';
import { Button } from '@plato/ui/primitives/button';
import { FilterPill, FilterPillBody, FilterPillRemove } from '@plato/ui/primitives/filter-controls';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from '@plato/ui/primitives/input-group';
import { cn } from '@plato/ui/util/classes';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { keepPreviousData } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { ListFilterIcon, SearchIcon, XIcon } from 'lucide-react';
import { useState, type ReactElement, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { api } from '../../../utils/api.ts';
import { cleanEmpty } from '../../../utils/clean-empty.ts';
import {
    useActivityFilters,
    type ActivityQueryFilters,
} from '../../activity-feed/hooks/use-activity-filters.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import {
    toActivityFilters,
    toFilterMenuSelection,
    useActivityFilterCategories,
    type ActivityFilterCategoryOptions,
} from '../hooks/use-activity-filter-categories.ts';
import { getActivePreset, withPreset, type ActivityFeedPreset } from '../lib/activity-feed-presets.ts';
import { ActivityFilterPresets, type ActivityFilterPreset } from './activity-filter-presets.tsx';

function getSearchPlaceholder(
    t: TFunction<'activity-feed'>,
    scope: ActivityFilterCategoryOptions['scope'],
): string {
    switch (scope) {
        case 'global':
            return t('search_placeholder__global');
        case 'customer':
            return t('search_placeholder__customer');
    }
}

export type ActivityFilterBarProps = ActivityFilterCategoryOptions & {
    /** Filter shortcuts on a row under the search, in this order. None when omitted. */
    presets?: ActivityFilterPreset[];
    className?: string;
    ref?: Ref<HTMLDivElement>;
};

/**
 * Search field and filter menu, then the filter shortcuts and the applied chips.
 * Everything lives in the URL, so applying or clearing refetches whatever
 * reads `useActivityFilters`; a shortcut only sets or clears a few of them.
 */
export function ActivityFilterBar({
    scope,
    showUserFilter,
    presets,
    className,
    ref,
}: ActivityFilterBarProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const { appliedFilters, setAppliedFilters } = useActivityFilters();
    const categories = useActivityFilterCategories({ scope, showUserFilter });
    const sendEvent = useAnalytics();
    const isDesktop = useBreakpoint('sm');

    // Where the menu is: closed when undefined, and a chip opens it on its own
    // category, anchored on the chip it was opened from.
    const [menu, setMenu] = useState<{ categoryKey?: string; anchor?: HTMLElement }>();

    const applyFilters = (values: ActivityQueryFilters): void => {
        const next = cleanEmpty(values);
        const payload = getUsedFilterEventPayload(next);
        if (payload.filters.length > 0) {
            if (scope === 'global') {
                sendEvent('global_activity_feed_filter_used', payload);
            } else {
                sendEvent('customer_activity_feed_filter_used', payload);
            }
        }
        setAppliedFilters(next);
    };

    // The field is local so typing stays responsive; the URL only catches up
    // once the typing stops.
    const [searchText, setSearchText] = useState(appliedFilters.searchText ?? '');
    useDebounce(
        () => {
            if (searchText !== (appliedFilters.searchText ?? '')) {
                applyFilters({ ...appliedFilters, searchText });
            }
        },
        300,
        [searchText],
    );

    // Category keys are the filter fields, so the menu's selection is a view of
    // the applied filters rather than a separate model.
    const selection: FilterMenuSelection = cleanEmpty(toFilterMenuSelection(appliedFilters));

    const applySelection = (next: FilterMenuSelection): void => {
        applyFilters({ ...appliedFilters, ...toActivityFilters(next) });
    };

    const changePreset = (preset: ActivityFeedPreset | undefined): void => {
        if (preset) {
            sendEvent('customer_detail_open_filter_clicked', {
                filter_type: preset === 'openOrders' ? 'open_orders' : 'open_quotes',
            });
        }
        setAppliedFilters(cleanEmpty(withPreset(appliedFilters, preset)));
    };

    // The menu cannot pick customers yet (it has no search), but a customer
    // filter from a link or the old page still narrows the global feed, so it
    // keeps a chip that can at least remove it. The customer feed ignores it.
    const customerIds = scope === 'global' ? (appliedFilters.customerIds ?? []) : [];
    const { data: filteredCustomers } = api.platoUser.getManyCustomers.getByIds.useQuery(
        { customerIds },
        { enabled: customerIds.length > 0, placeholderData: keepPreviousData },
    );

    // Only what the bar shows counts: a filter without a chip either does
    // nothing here or could not be removed.
    const appliedCount =
        customerIds.length +
        categories.reduce((total, category) => total + (selection[category.key]?.length ?? 0), 0);

    const customerChip =
        customerIds.length > 0
            ? [
                  <FilterPill
                      key="customerIds"
                      label={t('activity_feed__filter_customers', { ns: 'customer-detail' })}
                  >
                      <FilterPillBody>
                          {customerIds.length === 1
                              ? (filteredCustomers?.[0]?.customerName ?? customerIds[0])
                              : String(customerIds.length)}
                      </FilterPillBody>
                      <FilterPillRemove
                          aria-label={t('filter__remove', {
                              filter: t('activity_feed__filter_customers', { ns: 'customer-detail' }),
                          })}
                          onClick={() => {
                              applyFilters({ ...appliedFilters, customerIds: undefined });
                          }}
                      />
                  </FilterPill>,
              ]
            : [];

    const categoryChips = categories.flatMap((category) => {
        const values = selection[category.key] ?? [];

        if (values.length === 0) {
            return [];
        }

        // One value reads better spelled out; several collapse to a count.
        const first = category.options.find((option) => option.value === values[0]);
        const summary = values.length === 1 ? (first?.label ?? values[0]) : String(values.length);

        return [
            <FilterPill key={category.key} label={category.label}>
                <FilterPillBody
                    render={
                        <button
                            type="button"
                            onClick={(event) => {
                                setMenu({
                                    categoryKey: category.key,
                                    anchor: event.currentTarget,
                                });
                            }}
                        />
                    }
                >
                    {summary}
                </FilterPillBody>
                <FilterPillRemove
                    aria-label={t('filter__remove', { filter: category.label })}
                    onClick={() => {
                        applySelection({ ...selection, [category.key]: [] });
                    }}
                />
            </FilterPill>,
        ];
    });

    const chips = [...customerChip, ...categoryChips];

    const filterMenu = (
        <FilterMenu
            categories={categories}
            selection={selection}
            onApply={applySelection}
            open={menu !== undefined}
            onOpenChange={(open) => {
                setMenu(open ? {} : undefined);
            }}
            categoryKey={menu?.categoryKey}
            onCategoryKeyChange={(categoryKey) => {
                setMenu((current) => (current === undefined ? undefined : { ...current, categoryKey }));
            }}
        >
            <FilterMenuTrigger render={<Button variant="secondary" className="bg-container" />}>
                <ListFilterIcon />
                {t('filter__add')}
                {appliedCount > 0 && (
                    <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-tight text-ui-sm font-medium text-brand-contrast tabular-nums">
                        {appliedCount}
                    </span>
                )}
            </FilterMenuTrigger>
            <FilterMenuContent
                anchor={menu?.anchor}
                title={t('filter__title')}
                closeLabel={t('filter__close')}
                backLabel={t('filter__back')}
            >
                <FilterMenuFooter>
                    <FilterMenuClear>{t('filter__clear_all')}</FilterMenuClear>
                    <FilterMenuApply>{t('filter__apply')}</FilterMenuApply>
                </FilterMenuFooter>
            </FilterMenuContent>
        </FilterMenu>
    );

    return (
        <div ref={ref} className={cn('flex flex-col gap-cozy', className)}>
            <div className="flex items-center gap-cozy">
                {/* white on the page wash, like the cards below */}
                <InputGroup className="bg-container">
                    <InputGroupAddon>
                        <SearchIcon />
                    </InputGroupAddon>
                    <InputGroupInput
                        data-slot="input-group-control"
                        value={searchText}
                        placeholder={getSearchPlaceholder(t, scope)}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                    />
                    {searchText && (
                        <InputGroupAddon align="inline-end">
                            <InputGroupButton
                                aria-label={t('search__clear')}
                                onClick={() => {
                                    setSearchText('');
                                }}
                            >
                                <XIcon />
                            </InputGroupButton>
                        </InputGroupAddon>
                    )}
                </InputGroup>
                <div className="shrink-0">{filterMenu}</div>
            </div>
            {presets && (
                <ActivityFilterPresets
                    presets={presets}
                    active={getActivePreset(appliedFilters)}
                    onChange={changePreset}
                />
            )}
            {/* Phones only count the filters on the button; desktop spells them out. */}
            {isDesktop && chips.length > 0 && (
                <div className="flex flex-wrap items-center gap-cozy">{chips}</div>
            )}
        </div>
    );
}
